import { useEffect, useMemo, useState } from 'react';
import { Boxes, Plus } from 'lucide-react';
import { AutocompleteSelect, Button, Modal } from '../../ui';
import { AppEnvironment, type CreateApplicationInput } from '../../gql/types';
import { TextField } from '../shared/FormField';
import { InlineBanner } from '../shared/InlineBanner';

// Create form for an application under a given client (US-8.1.1, W1). Name is required;
// environment defaults to DEVELOPMENT. clientId is supplied by the page (URL param) and is
// never user-editable here — applications always belong to the client whose detail view
// hosts this modal. Save is blocked until a non-empty name is present; server errors are
// surfaced inline.

const ENVIRONMENT_OPTIONS = [
  { value: AppEnvironment.Development, label: 'Development' },
  { value: AppEnvironment.Staging, label: 'Staging' },
  { value: AppEnvironment.Production, label: 'Production' },
];

function isEnvironment(value: string): value is AppEnvironment {
  return (Object.values(AppEnvironment) as string[]).includes(value);
}

export interface ApplicationFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientId: string;
  clientName?: string | undefined;
  loading?: boolean | undefined;
  errorMessage?: string | undefined;
  onSubmit: (input: CreateApplicationInput) => void;
}

export function ApplicationFormModal({
  isOpen,
  onClose,
  clientId,
  clientName,
  loading = false,
  errorMessage,
  onSubmit,
}: ApplicationFormModalProps) {
  const [name, setName] = useState('');
  const [environment, setEnvironment] = useState<AppEnvironment>(AppEnvironment.Development);
  const [attempted, setAttempted] = useState(false);

  // (Re)seed the form whenever the modal opens so a reopened modal starts clean.
  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setEnvironment(AppEnvironment.Development);
    setAttempted(false);
  }, [isOpen]);

  const trimmedName = name.trim();
  const valid = trimmedName.length > 0;

  const subtitle = useMemo(
    () =>
      clientName
        ? `Group API keys for an integration under ${clientName}.`
        : 'Group API keys for an integration under this client.',
    [clientName],
  );

  const handleSubmit = () => {
    setAttempted(true);
    if (!valid) return;
    const input: CreateApplicationInput = {
      clientId,
      name: trimmedName,
      environment,
    };
    onSubmit(input);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New application"
      subtitle={subtitle}
      icon={<Boxes className="h-7 w-7" aria-hidden="true" />}
      footer={
        <>
          <Button variant="secondary" fullWidth onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            fullWidth
            loading={loading}
            disabled={!valid}
            onClick={handleSubmit}
            leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
          >
            Create application
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {errorMessage && (
          <InlineBanner tone="error" testId="application-form-error">
            Couldn’t create the application: {errorMessage}
          </InlineBanner>
        )}

        <TextField
          label="Application name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Acme Web"
          error={attempted && !valid ? 'Enter a name for the application.' : undefined}
        />

        <AutocompleteSelect
          label="Environment"
          value={environment}
          options={ENVIRONMENT_OPTIONS}
          onChange={(next) => {
            if (isEnvironment(next)) setEnvironment(next);
          }}
        />
      </div>
    </Modal>
  );
}
